import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Settings, CreditCard, Truck, Loader2, Save, Eye, EyeOff,
  Zap, Crown, DollarSign, Image, Cloud
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showPaypalKey, setShowPaypalKey] = useState(false);
  const [showCloudinaryKey, setShowCloudinaryKey] = useState(false);
  
  const [settings, setSettings] = useState({
    trial_days: 30,
    stripe_enabled: false,
    stripe_public_key: "",
    stripe_secret_key: "",
    paypal_enabled: false,
    paypal_client_id: "",
    paypal_secret: "",
    cloudinary_enabled: false,
    cloudinary_cloud_name: "",
    cloudinary_api_key: "",
    cloudinary_api_secret: "",
    plans: {
      starter: { name: "Starter", price: 10.00, max_vehicles: 5, features: ["5 veicoli", "Supporto email", "Dashboard base"] },
      professional: { name: "Professional", price: 20.00, max_vehicles: 15, features: ["15 veicoli", "Supporto prioritario", "Statistiche avanzate", "Badge verificato"] },
      enterprise: { name: "Enterprise", price: 50.00, max_vehicles: -1, features: ["Veicoli illimitati", "Supporto dedicato 24/7", "API access", "Account manager"] }
    }
  });

  useEffect(() => {
    if (token) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSettings = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/admin/settings`, { headers, withCredentials: true });
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento impostazioni");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/settings`, settings, { headers, withCredentials: true });
      toast.success("Impostazioni salvate con successo");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = (planId, field, value) => {
    setSettings(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [planId]: {
          ...prev.plans[planId],
          [field]: field === "price" || field === "max_vehicles" ? parseFloat(value) || 0 : value
        }
      }
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Impostazioni">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Impostazioni">
      <div className="space-y-6" data-testid="admin-settings">
        <Tabs defaultValue="plans" className="w-full">
          <TabsList className="grid w-full grid-cols-4 max-w-lg">
            <TabsTrigger value="plans">Piani</TabsTrigger>
            <TabsTrigger value="payments">Pagamenti</TabsTrigger>
            <TabsTrigger value="storage">Storage</TabsTrigger>
            <TabsTrigger value="general">Generale</TabsTrigger>
          </TabsList>

          {/* Plans Tab */}
          <TabsContent value="plans" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Piani di Abbonamento
                </CardTitle>
                <CardDescription>
                  Configura prezzi e limiti per ogni piano
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Starter Plan */}
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Truck className="h-5 w-5 text-primary" />
                    <h3 className="font-bold">Starter</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Nome Piano</Label>
                      <Input
                        value={settings.plans.starter?.name || "Starter"}
                        onChange={(e) => updatePlan("starter", "name", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Prezzo (EUR/mese)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.plans.starter?.price || 10}
                        onChange={(e) => updatePlan("starter", "price", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Max Veicoli</Label>
                      <Input
                        type="number"
                        value={settings.plans.starter?.max_vehicles || 5}
                        onChange={(e) => updatePlan("starter", "max_vehicles", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Plan */}
                <div className="p-4 border border-primary rounded-lg bg-primary/5">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="h-5 w-5 text-primary" />
                    <h3 className="font-bold">Professional</h3>
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">Consigliato</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Nome Piano</Label>
                      <Input
                        value={settings.plans.professional?.name || "Professional"}
                        onChange={(e) => updatePlan("professional", "name", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Prezzo (EUR/mese)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.plans.professional?.price || 20}
                        onChange={(e) => updatePlan("professional", "price", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Max Veicoli</Label>
                      <Input
                        type="number"
                        value={settings.plans.professional?.max_vehicles || 15}
                        onChange={(e) => updatePlan("professional", "max_vehicles", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Enterprise Plan */}
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown className="h-5 w-5 text-primary" />
                    <h3 className="font-bold">Enterprise</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Nome Piano</Label>
                      <Input
                        value={settings.plans.enterprise?.name || "Enterprise"}
                        onChange={(e) => updatePlan("enterprise", "name", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Prezzo (EUR/mese)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.plans.enterprise?.price || 50}
                        onChange={(e) => updatePlan("enterprise", "price", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Max Veicoli (-1 = illimitati)</Label>
                      <Input
                        type="number"
                        value={settings.plans.enterprise?.max_vehicles || -1}
                        onChange={(e) => updatePlan("enterprise", "max_vehicles", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-4 mt-6">
            {/* Stripe */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Stripe
                    </CardTitle>
                    <CardDescription>
                      Pagamenti con carte di credito
                    </CardDescription>
                  </div>
                  <Switch
                    checked={settings.stripe_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, stripe_enabled: checked }))}
                  />
                </div>
              </CardHeader>
              {settings.stripe_enabled && (
                <CardContent className="space-y-4">
                  <div>
                    <Label>Publishable Key</Label>
                    <Input
                      placeholder="pk_live_... o pk_test_..."
                      value={settings.stripe_public_key}
                      onChange={(e) => setSettings(prev => ({ ...prev, stripe_public_key: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Secret Key</Label>
                    <div className="relative">
                      <Input
                        type={showStripeKey ? "text" : "password"}
                        placeholder="sk_live_... o sk_test_..."
                        value={settings.stripe_secret_key}
                        onChange={(e) => setSettings(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        onClick={() => setShowStripeKey(!showStripeKey)}
                      >
                        {showStripeKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Ottieni le chiavi da{" "}
                    <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      dashboard.stripe.com/apikeys
                    </a>
                  </p>
                </CardContent>
              )}
            </Card>

            {/* PayPal */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                      </svg>
                      PayPal
                    </CardTitle>
                    <CardDescription>
                      Pagamenti con PayPal
                    </CardDescription>
                  </div>
                  <Switch
                    checked={settings.paypal_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, paypal_enabled: checked }))}
                  />
                </div>
              </CardHeader>
              {settings.paypal_enabled && (
                <CardContent className="space-y-4">
                  <div>
                    <Label>Client ID</Label>
                    <Input
                      placeholder="Client ID PayPal"
                      value={settings.paypal_client_id}
                      onChange={(e) => setSettings(prev => ({ ...prev, paypal_client_id: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Secret</Label>
                    <div className="relative">
                      <Input
                        type={showPaypalKey ? "text" : "password"}
                        placeholder="Secret PayPal"
                        value={settings.paypal_secret}
                        onChange={(e) => setSettings(prev => ({ ...prev, paypal_secret: e.target.value }))}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        onClick={() => setShowPaypalKey(!showPaypalKey)}
                      >
                        {showPaypalKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Ottieni le credenziali da{" "}
                    <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      developer.paypal.com
                    </a>
                  </p>
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Storage Tab - Cloudinary */}
          <TabsContent value="storage" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Cloud className="h-5 w-5" />
                      Cloudinary
                    </CardTitle>
                    <CardDescription>
                      Storage cloud per le immagini dei veicoli
                    </CardDescription>
                  </div>
                  <Switch
                    checked={settings.cloudinary_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, cloudinary_enabled: checked }))}
                  />
                </div>
              </CardHeader>
              {settings.cloudinary_enabled && (
                <CardContent className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Consigliato!</strong> Cloudinary permette di salvare le immagini in modo permanente nel cloud. 
                      Senza Cloudinary, le immagini vengono perse ad ogni redeploy.
                    </p>
                  </div>
                  <div>
                    <Label>Cloud Name</Label>
                    <Input
                      placeholder="Il tuo cloud name"
                      value={settings.cloudinary_cloud_name}
                      onChange={(e) => setSettings(prev => ({ ...prev, cloudinary_cloud_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>API Key</Label>
                    <Input
                      placeholder="API Key"
                      value={settings.cloudinary_api_key}
                      onChange={(e) => setSettings(prev => ({ ...prev, cloudinary_api_key: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>API Secret</Label>
                    <div className="relative">
                      <Input
                        type={showCloudinaryKey ? "text" : "password"}
                        placeholder="API Secret"
                        value={settings.cloudinary_api_secret}
                        onChange={(e) => setSettings(prev => ({ ...prev, cloudinary_api_secret: e.target.value }))}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        onClick={() => setShowCloudinaryKey(!showCloudinaryKey)}
                      >
                        {showCloudinaryKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Crea un account gratuito e ottieni le credenziali da{" "}
                    <a href="https://cloudinary.com/console" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      cloudinary.com/console
                    </a>
                  </p>
                </CardContent>
              )}
            </Card>

            {!settings.cloudinary_enabled && (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Image className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-bold mb-2">Storage Locale</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Le immagini vengono salvate sul server locale.<br />
                    <span className="text-amber-600">Attenzione: verranno perse ad ogni redeploy!</span>
                  </p>
                  <Button 
                    variant="outline"
                    onClick={() => setSettings(prev => ({ ...prev, cloudinary_enabled: true }))}
                  >
                    Attiva Cloudinary
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Impostazioni Generali
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-xs">
                  <Label>Giorni di Prova Gratuita</Label>
                  <Input
                    type="number"
                    value={settings.trial_days}
                    onChange={(e) => setSettings(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 30 }))}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={saving} className="btn-primary">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salva Impostazioni
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
